// frontend/src/pages/recruiter/RecruiterApplicants.jsx
import { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "./../../components/ui/button";
import { Card, CardContent, CardHeader } from "./../../components/ui/card";
import { Badge } from "./../../components/ui/badge";
import { Input } from "./../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./../../components/ui/select";
import { Alert, AlertDescription } from "./../../components/ui/alert";
import { Loader2, CheckCircle, XCircle, Users, Calendar, Clock, Download, FileText, Zap } from "lucide-react";
import api from "./../../api/client";
import RecruiterPageShell from "../../components/recruiter/RecruiterPageShell";
import RecruiterEmptyState from "../../components/recruiter/RecruiterEmptyState";
import Loader from "../../components/ui/Loader";
import { glassCard, glassCardHover, glassPanel } from "../../utils/glassStyles";

export default function RecruiterApplicants() {
 const { jobId } = useParams();
 const navigate = useNavigate();
 const [applicants, setApplicants] = useState([]);
 const [job, setJob] = useState(null);
 const [loading, setLoading] = useState(true);
 const [actionLoading, setActionLoading] = useState({});
 const [success, setSuccess] = useState("");
 const [error, setError] = useState("");

 // Search & Filter
 const [search, setSearch] = useState("");
 const [statusFilter, setStatusFilter] = useState("ALL");
 const [gpaFilter, setGpaFilter] = useState("");
 const [universityFilter, setUniversityFilter] = useState("");


 const fetchApplicants = useCallback(async () => {
 try {
 const [appsRes, jobRes] = await Promise.all([
 api.get(`/recruiter/jobs/${jobId}/applicants`),
 api.get(`/jobs/${jobId}`),
 ]);
 setApplicants(appsRes.data);
 setJob(jobRes.data);
 } catch (err) {
 setError("Failed to load applicants");
 } finally {
 setLoading(false);
 }
 }, [jobId]);

 useEffect(() => {
 fetchApplicants();
 }, [fetchApplicants]);

 const handleStatusChange = async (appId, status) => {
 setActionLoading((prev) => ({ ...prev, [appId]: true }));
 try {
 await api.patch(`/recruiter/applications/${appId}`, { status });
 setSuccess(`Application ${status.toLowerCase()}`);
 fetchApplicants();
 } catch (err) {
 setError(err.response?.data?.message || "Action failed");
 } finally {
 setActionLoading((prev) => ({ ...prev, [appId]: false }));
 setTimeout(() => setSuccess(""), 3000);
 }
 };

 // Resume download with debug
 const downloadResume = async (url, applierName) => {
 try {
 const res = await fetch(url);

 if (!res.ok) {
 throw new Error(`HTTP ${res.status}`);
 }

 const blob = await res.blob();

 const link = document.createElement("a");
 const fileExtension = url.split(".").pop();
 const filename = `${applierName || "resume"}.${fileExtension}`;
 link.href = window.URL.createObjectURL(blob);
 link.download = filename;
 document.body.appendChild(link);
 link.click();
 document.body.removeChild(link);
 } catch (err) {
 alert(`Failed to download resume. File may not exist.\n${err.message}`);
 }
 };

 const filteredApplicants = useMemo(() => {
 return applicants.filter((app) => {
 const applier = app.applier;
 const profile = applier?.applierProfile;
 
 const matchesSearch =
 (profile?.fullName?.toLowerCase().includes(search.toLowerCase()) ||
 applier.email.toLowerCase().includes(search.toLowerCase()));
 const matchesStatus = statusFilter === "ALL" || app.status === statusFilter;
 const matchesGpa = !gpaFilter || (profile?.gpa && profile.gpa >= Number(gpaFilter));
 const matchesUni = !universityFilter || 
 (profile?.university && profile.university.toLowerCase().includes(universityFilter.toLowerCase()));
 return matchesSearch && matchesStatus && matchesGpa && matchesUni;
 });
 }, [applicants, search, statusFilter, gpaFilter, universityFilter]);

 const getStatusBadge = (status) => {
 const map = {
 PENDING: { variant: "secondary", text: "Pending", icon: Clock },
 ACCEPTED: { variant: "default", text: "Accepted", icon: CheckCircle },
 REJECTED: { variant: "destructive", text: "Rejected", icon: XCircle },
 };
 const s = map[status] || map.PENDING;
 const Icon = s.icon;
 return (
 <Badge variant={s.variant} className="flex items-center gap-1">
 <Icon className="w-3 h-3" />
 {s.text}
 </Badge>
 );
 };

 if (loading) {
 return <Loader label="Loading applicants..." />;
 }

 return (
 <RecruiterPageShell
 title={job?.title || "Job Applicants"}
 subtitle={job?.companyName || "Review candidates for this role"}
 onBack={() => navigate("/recruiter/dashboard")}
 actions={
 <div className={`rounded-2xl px-4 py-3 text-center ${glassCard}`}>
 <p className="text-2xl font-bold text-primary">{filteredApplicants.length}</p>
 <p className="text-xs text-muted-foreground">Applicants</p>
 </div>
 }
 >
 {/* Search & Filters */}
 <Card className={`mb-6 rounded-2xl ${glassPanel}`}>
 <CardContent className="p-4 space-y-4">
 <div className="flex flex-col sm:flex-row gap-3">
 <div className="flex-1 relative">
 <Input
 placeholder="Search by name or email..."
 value={search}
 onChange={(e) => setSearch(e.target.value)}
 className=" text-foreground"
 />
 </div>
 <Select value={statusFilter} onValueChange={setStatusFilter}>
 <SelectTrigger className="w-full sm:w-40">
 <SelectValue />
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="ALL">All Status</SelectItem>
 <SelectItem value="PENDING">Pending</SelectItem>
 <SelectItem value="ACCEPTED">Accepted</SelectItem>
 <SelectItem value="REJECTED">Rejected</SelectItem>
 </SelectContent>
 </Select>
 </div>
 <div className="flex flex-col sm:flex-row gap-3">
 <Input
 type="number"
 step="0.1"
 min="0"
 max="4"
 placeholder="Min GPA"
 value={gpaFilter}
 onChange={(e) => setGpaFilter(e.target.value)}
 className="w-full sm:w-32"
 />
 <Input
 placeholder="University"
 value={universityFilter}
 onChange={(e) => setUniversityFilter(e.target.value)}
 className="flex-1"
 />
 </div>
 </CardContent>
 </Card>

 {success && (
 <Alert className="mb-6 border-green-200 dark:border-green-800 bg-green-500/10 ">
 <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
 <AlertDescription className="text-green-800 dark:text-green-300">{success}</AlertDescription>
 </Alert>
 )}
 {error && (
 <Alert variant="destructive" className="mb-6">
 <AlertDescription>{error}</AlertDescription>
 </Alert>
 )}

 {filteredApplicants.length === 0 ? (
 <RecruiterEmptyState
 icon={Users}
 title="No applicants match your filters"
 description="Try adjusting your search or filter criteria."
 />
 ) : (
 <div className="space-y-4 sm:space-y-6">
 {filteredApplicants.map((app) => {
 const applier = app.applier;
 return (
 <Card key={app.id} className={`rounded-2xl ${glassCardHover}`}>
 <CardHeader className="pb-4">
 <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
 <div className="flex-1 min-w-0">
 <p 
 className="font-semibold text-lg sm:text-xl text-foreground hover:text-primary transition-colors cursor-pointer truncate"
 onClick={() => navigate(`/recruiter/applications/${app.id}`)}
 >
 {applier.applierProfile?.fullName || applier.email}
 </p>
 <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1 truncate">
 {applier.email}
 </p>
 {applier.applierProfile?.telegramUsername && (
 <p className="text-sm text-primary font-medium mt-0.5">
 @{applier.applierProfile.telegramUsername}
 </p>
 )}
 
 {/* Metadata - Responsive */}
 <div className="flex flex-wrap gap-x-4 gap-y-2 text-xs sm:text-sm text-muted-foreground mt-3">
 <span className="flex items-center gap-1">
 <Calendar className="w-3 h-3 sm:w-4 sm:h-4" />
 {new Date(app.appliedAt).toLocaleDateString()}
 </span>
 {applier.applierProfile?.gpa && (
 <span className="text-primary font-semibold">
 GPA: {applier.applierProfile.gpa}
 </span>
 )}
 {applier.applierProfile?.university && (
 <span className="truncate max-w-[200px]">
 {applier.applierProfile.university}
 </span>
 )}
 </div>

 {/* Cover Letter */}
 {app.coverLetter && (
 <div className="mt-4 p-3 sm:p-4 rounded-xl border border-border/50">
 <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5">
 <FileText className="w-3 h-3 sm:w-3.5 sm:h-3.5" /> 
 Cover Letter
 </p>
 <p className="text-xs sm:text-sm text-foreground whitespace-pre-wrap leading-relaxed line-clamp-4 sm:line-clamp-none">
 {app.coverLetter}
 </p>
 </div>
 )}

 {/* Why Me */}
 {app.whyMe && (
 <div className="mt-3 p-3 sm:p-4 bg-primary/5 rounded-xl border border-primary/20">
 <p className="text-xs font-bold text-primary uppercase tracking-wider mb-2 flex items-center gap-1.5">
 <Zap className="w-3 h-3 sm:w-3.5 sm:h-3.5" /> 
 Why Me?
 </p>
 <p className="text-xs sm:text-sm text-foreground whitespace-pre-wrap leading-relaxed line-clamp-4 sm:line-clamp-none">
 {app.whyMe}
 </p>
 </div>
 )}
 </div>
 
 {/* Status Badge */}
 <div className="self-start">
 {getStatusBadge(app.status)}
 </div>
 </div>
 </CardHeader>
 
 <CardContent className="pt-0">
 <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
 {/* Resume Button */}
 <div>
 {app.applier?.applierProfile?.resumeUrl ? (
 <Button
 variant="outline"
 size="sm"
 className="w-full sm:w-auto"
 onClick={() => {
 const rawUrl = app.applier.applierProfile.resumeUrl;
 const baseUrl = ((process.env.NODE_ENV === 'development' ? 'http://localhost:5000' : process.env.REACT_APP_API_URL) || "http://localhost:5000").replace(/\/$/, "");
 const normalizedPath = rawUrl.startsWith("/") ? rawUrl : `/${rawUrl}`;
 const fullUrl = rawUrl.startsWith("http") ? rawUrl : `${baseUrl}${normalizedPath}`;
 window.open(fullUrl, "_blank");
 }}
 >
 <Download className="w-4 h-4 mr-2" />
 Download Resume
 </Button>
 ) : (
 <span className="text-sm text-muted-foreground">No resume uploaded</span>
 )}
 </div>
 
 {/* Action Buttons */}
 {app.status === "PENDING" && (
 <div className="flex gap-2 w-full sm:w-auto">
 <Button
 size="sm"
 variant="default"
 className="flex-1 sm:flex-initial bg-green-600 hover:bg-green-700 text-white"
 onClick={() => handleStatusChange(app.id, "ACCEPTED")}
 disabled={actionLoading[app.id]}
 >
 {actionLoading[app.id] ? (
 <Loader2 className="w-4 h-4 animate-spin" />
 ) : (
 <>
 <CheckCircle className="w-4 h-4 mr-1" />
 Accept
 </>
 )}
 </Button>
 <Button
 size="sm"
 variant="destructive"
 className="flex-1 sm:flex-initial"
 onClick={() => handleStatusChange(app.id, "REJECTED")}
 disabled={actionLoading[app.id]}
 >
 {actionLoading[app.id] ? (
 <Loader2 className="w-4 h-4 animate-spin" />
 ) : (
 <>
 <XCircle className="w-4 h-4 mr-1" />
 Reject
 </>
 )}
 </Button>
 </div>
 )}
 </div>
 </CardContent>
 </Card>
 );
 })}
 </div>
 )}
 </RecruiterPageShell>
 );
}
